// The arrival trail: a short mono line whose marked part ("Governance in Motion") arrives with three blurred copies
// settling in from the left, a streak that reads as motion. The story's hero eyebrow has carried it since the
// landing was built; the Desert Book footer's last line carries it too (Chris, 2026-10-25: "the same effect there").
// The label itself stays crisp; the copies are decoration, hidden under reduced motion. With `onView` the streak
// waits until the line scrolls into view, so a footer gets its arrival when the reader reaches it.

import { useEffect, useRef, useState } from "react";

const STREAK: [number, number, number][] = [[-7, 0.32, 0.6], [-15, 0.18, 1.4], [-25, 0.09, 2.4]]; // x offset, resting opacity, blur

const CSS = "@keyframes opening-streak{from{opacity:0;transform:translateX(calc(var(--sx) - 36px))}to{opacity:var(--so);transform:translateX(var(--sx))}}.op-streak{position:absolute;top:0;left:0;padding:inherit;white-space:nowrap;pointer-events:none;opacity:0}.op-streak.on{animation:opening-streak 1.8s cubic-bezier(.16,1,.3,1) .4s forwards}@media (prefers-reduced-motion:reduce){.op-streak{display:none}}";

export default function Trail({ head, mark, after, onView = false, markWeight = 700, every = 9000 }: {
  /** The plain part before the mark, e.g. "Governance at Rest and". */
  head?: string;
  /** The part that carries the streak, e.g. "Governance in Motion". */
  mark: string;
  /** Anything after the mark, e.g. a period. */
  after?: string;
  /** Start the streak when the line scrolls into view rather than on mount. */
  onView?: boolean;
  markWeight?: number;
  /** The streak runs again every so often while the line is on screen (Chris, 2026-10-25: "reblur or move every so
   *  often, just that element"); 0 turns the repeat off. Reduced motion never shows the streak at all. */
  every?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [on, setOn] = useState(!onView);
  const [visible, setVisible] = useState(!onView);
  const [run, setRun] = useState(0); // a new key re-mounts the copies, so the animation plays again
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setOn(true); setVisible(true); return; }
    const io = new IntersectionObserver((es) => {
      const seen = es.some((e) => e.isIntersecting);
      setVisible(seen);
      if (seen) setOn(true);
    }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!every || !on || !visible) return;
    const id = window.setInterval(() => setRun((r) => r + 1), every);
    return () => window.clearInterval(id);
  }, [every, on, visible]);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", overflow: "hidden", verticalAlign: "bottom" }}>
      <style>{CSS}</style>
      {head && <span style={{ opacity: 0.82, marginRight: "0.5em" }}>{head.trimEnd()}</span>}
      <span style={{ position: "relative", display: "inline-block", fontWeight: markWeight }}>
        {STREAK.map(([x, o, b], k) => (
          <span key={`${run}-${k}`} aria-hidden className={`op-streak${on ? " on" : ""}`} style={{ filter: `blur(${b}px)`, animationDelay: `${(run ? 0 : 0.4) + k * 0.12}s`, "--sx": `${x}px`, "--so": o } as React.CSSProperties}>{mark}</span>
        ))}
        <span style={{ position: "relative" }}>{mark}</span>
      </span>
      {after}
    </span>
  );
}
